/**
 * Typed catalog of feature-flag keys.
 *
 * As of the features-app cutover, the *access level* for each flag lives in
 * the backend (``Feature.access_level``) and is fetched at runtime via
 * ``useFeatureFlags``. This file just keeps a typed list of the keys the
 * frontend code references, so call sites stay compile-time-checked:
 *
 *     useFeatureAccess(FEATURE_FLAGS.PUNCH_LISTS)
 *
 * To add a new key:
 *   1. Add a row in this map.
 *   2. Add a matching ``Feature`` row in the backend admin (or seed it via a
 *      data migration in ``backend/features/migrations/``).
 *
 * Removing a key is just deletion in this file plus retiring the row server
 * side — the frontend never has to redeploy in lock-step with the backend.
 */

export type FeatureAccessLevel = 'all' | 'admin' | 'staff' | 'in_development';

export interface FeatureFlag {
  /**
   * Stable lowercase identifier shared with the backend's
   * ``Feature.flag_key`` field. Must match the ``[a-z0-9_]+`` pattern the
   * backend validates.
   */
  id: string;
}

export const FEATURE_FLAGS = {
  // Staff Only
  ADD_NEW: { id: 'add_new' },
  ADD_SCAN: { id: 'add_scan' },
  ADD_LAYOUT: { id: 'add_layout' },
  SURVEILLANCE_REPORT: { id: 'surveillance_report' },
  UPLOAD_DRONE_VIDEO: { id: 'upload_drone_video' },
  LEADS_CAPTURE: { id: 'leads_capture' },
  ALL_CLIENTS: { id: 'all_clients' },
  ALL_PROJECTS: { id: 'all_projects' },

  // Admin Only
  ADMIN_PANEL: { id: 'admin_panel' },

  // All Users
  SUPPORT: { id: 'support' },
  TRAINING: { id: 'training' },
  /**
   * The "In Progress" tab of the Interactive Viewer route — site photos
   * pinned to layout markers across construction dates. Currently
   * available to all users; the feature flag exists so guided tours and
   * future product surfaces can hang off a stable Feature row.
   */
  IN_PROGRESS_VIEWER: { id: 'in_progress_viewer' },
  /**
   * The "Asbuilds" tab of the Interactive Viewer route — as-built site
   * photos pinned to layout markers. Same product surface as
   * ``IN_PROGRESS_VIEWER``, different content stream.
   */
  ASBUILDS_VIEWER: { id: 'asbuilds_viewer' },
  /**
   * The Drone Tours page — aerial 360° tours captured at different
   * points in time. Same auto-start / guided-tour treatment as the
   * Interactive Viewer.
   */
  DRONES_VIEWER: { id: 'drones_viewer' },

  // In Development
  PUNCH_LISTS: { id: 'punch_lists' },
  OBSERVATIONS: { id: 'observations' },
  SAFETY: { id: 'safety' },
  PHOTO_GALLERY: { id: 'photo_gallery' },
  DRAWINGS: { id: 'drawings' },
  RFIS: { id: 'rfis' },
  SUBMITTALS: { id: 'submittals' },
  FILES: { id: 'files' },
  DAILY_LOG: { id: 'daily_log' },
  MEETING_MINUTES: { id: 'meeting_minutes' },
} as const satisfies Record<string, FeatureFlag>;

export type FeatureFlagName = keyof typeof FEATURE_FLAGS;
export type FeatureFlagKey = (typeof FEATURE_FLAGS)[FeatureFlagName]['id'];
